import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors } } = useForm({
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      phone: user?.phone || ''
    }
  });

  const { register: registerPassword, handleSubmit: handlePasswordSubmit, formState: { errors: passwordErrors }, reset: resetPassword, watch } = useForm();

  const newPassword = watch('newPassword');

  const onProfileSubmit = async (data) => {
    setLoading(true);
    await updateProfile(data);
    setLoading(false);
  };

  const onPasswordSubmit = async (data) => {
    setPasswordLoading(true);
    const result = await changePassword(data.currentPassword, data.newPassword);
    if (result.success) {
      resetPassword();
    }
    setPasswordLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600">Manage your account settings</p>
      </div>

      {/* Profile Info */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium">Profile Information</h2>
        </div>
        <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">First Name</label>
              <input
                type="text"
                className={`input ${profileErrors.firstName ? 'input-error' : ''}`}
                {...registerProfile('firstName', { required: 'First name is required' })}
              />
              {profileErrors.firstName && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="label">Last Name</label>
              <input
                type="text"
                className={`input ${profileErrors.lastName ? 'input-error' : ''}`}
                {...registerProfile('lastName', { required: 'Last name is required' })}
              />
              {profileErrors.lastName && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={user?.email}
              disabled
              className="input bg-gray-100"
            />
            <p className="mt-1 text-sm text-gray-500">Contact admin to change your email</p>
          </div>

          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              className="input"
              {...registerProfile('phone')}
            />
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium">Change Password</h2>
        </div>
        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="card-body space-y-4">
          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              className={`input ${passwordErrors.currentPassword ? 'input-error' : ''}`}
              {...registerPassword('currentPassword', { required: 'Current password is required' })}
            />
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              className={`input ${passwordErrors.newPassword ? 'input-error' : ''}`}
              {...registerPassword('newPassword', {
                required: 'New password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                  message: 'Password must contain uppercase, lowercase, number, and special character'
                }
              })}
            />
            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              className={`input ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
              {...registerPassword('confirmPassword', {
                required: 'Please confirm your password',
                validate: value => value === newPassword || 'Passwords do not match'
              })}
            />
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={passwordLoading} className="btn-primary">
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Account Info */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium">Account Information</h2>
        </div>
        <div className="card-body">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Role</dt>
              <dd className="font-medium">{user?.role}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Hotels</dt>
              <dd className="font-medium">
                {user?.hotels?.map(h => h.name).join(', ') || 'None assigned'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Teams</dt>
              <dd className="font-medium">
                {user?.teams?.map(t => t.name).join(', ') || 'None assigned'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default Profile;
